import { z, any } from 'zod';
import { languages } from './const.js';
export const isLanguageTag = (value) => typeof value === 'string' &&
    Object.keys(languages)
        .map((key) => key.toLowerCase())
        .includes(value.toLowerCase());
export const languageTagGuard = z
    .any()
    .refine((value) => isLanguageTag(value));
/**
 * Normalizes a language tag using Intl.getCanonicalLocales.
 * Transforms underscores to hyphens and returns the canonical form.
 * Returns undefined for non-string inputs, empty strings, '*' wildcard, or invalid language tags.
 */
export const canonicalizeLanguageTag = (language) => {
    if (typeof language !== 'string') {
        return;
    }
    const trimmed = language.trim();
    if (!trimmed || trimmed === '*') {
        return;
    }
    try {
        const [canonical] = Intl.getCanonicalLocales(trimmed.replaceAll('_', '-'));
        return canonical;
    }
    catch {
        // Intentionally ignore errors - invalid language tags should return undefined
    }
};
const isNonNullable = (value) => value !== null && value !== undefined;
const toSupportedLanguageEntry = (language) => {
    const canonical = canonicalizeLanguageTag(language);
    if (!canonical) {
        return;
    }
    const [base = canonical] = canonical.split('-');
    return {
        original: language,
        canonical: canonical.toLowerCase(),
        base: base.toLowerCase(),
    };
};
const findMatchingSupportedLanguage = (preferred, supportedEntries) => {
    for (const language of preferred) {
        const canonicalPreferred = canonicalizeLanguageTag(language)?.toLowerCase();
        if (!canonicalPreferred) {
            continue;
        }
        const directMatch = supportedEntries.find((entry) => entry.canonical === canonicalPreferred);
        if (directMatch) {
            return {
                entry: directMatch,
                matchType: 'exact',
            };
        }
        const [base = canonicalPreferred] = canonicalPreferred.split('-');
        const baseMatch = supportedEntries.find((entry) => entry.base === base);
        if (baseMatch) {
            return {
                entry: baseMatch,
                matchType: 'base',
            };
        }
    }
};
const buildSupportedEntries = (supported) => supported
    .map((language) => toSupportedLanguageEntry(language))
    .filter((entry) => isNonNullable(entry));
export const matchExactLanguageTag = (preferred, supported) => {
    const supportedEntries = buildSupportedEntries(supported);
    for (const language of preferred) {
        const canonicalPreferred = canonicalizeLanguageTag(language)?.toLowerCase();
        if (!canonicalPreferred) {
            continue;
        }
        const directMatch = supportedEntries.find((entry) => entry.canonical === canonicalPreferred);
        if (directMatch) {
            return directMatch.original;
        }
    }
};
/**
 * Matches preferred language tags against supported languages.
 * Attempts direct matches first, then falls back to base language matches.
 * Returns undefined when no match is found (unlike findSupportedLanguageTag which returns a default).
 *
 * @param preferred - Iterable of preferred language tags
 * @param supported - Array of supported language tags
 * @returns The matching supported language tag, or undefined if no match is found
 */
export const matchSupportedLanguageTag = (preferred, supported) => {
    const supportedEntries = buildSupportedEntries(supported);
    const matched = findMatchingSupportedLanguage(preferred, supportedEntries);
    return {
        supportedEntries,
        match: matched?.entry.original,
        matchType: matched?.matchType,
    };
};
/**
 * Finds the best matching language tag from preferred languages that is supported.
 * Attempts direct matches first, then falls back to base language matches.
 * If no match is found, returns the default language or a supported version of it.
 *
 * @param preferred - Iterable of preferred language tags (e.g., from Accept-Language header)
 * @param supported - Array of supported language tags
 * @param defaultLanguage - Default language to use when no match is found (default: 'en')
 * @returns The best matching supported language tag
 */
export const findSupportedLanguageTag = (preferred, supported, defaultLanguage = 'en') => {
    const { match: matched, supportedEntries } = matchSupportedLanguageTag(preferred, supported);
    if (matched) {
        return matched;
    }
    const canonicalDefault = canonicalizeLanguageTag(defaultLanguage)?.toLowerCase();
    const defaultMatch = canonicalDefault
        ? (supportedEntries.find((entry) => entry.canonical === canonicalDefault)?.original ??
            supportedEntries.find((entry) => entry.base === canonicalDefault)?.original)
        : undefined;
    return defaultMatch ?? defaultLanguage;
};
/**
 * https://github.com/colinhacks/zod/issues/316#issuecomment-850906479
 * Create a schema matches anything and returns a value. Use it with `or`:
 *
 * const schema = zod.number();
 * const tolerant = schema.or(fallback(-1));
 *
 * schema.parse('foo')      // => ZodError
 * tolerant.parse('foo')    // -1
 */
export function fallback(value) {
    return any().transform(() => value);
}
